<template>
  <div>
    <div class="hide-empty-switch-container">
      <label>{{ t('kksVgbmForm.hideEmpty') }}</label>
      <PVToggleSwitch v-model="isHideEmpty" />
    </div>
    <table-wrapper
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-display="false"
      :items="filteredVgbmAssignments"
      :loading="!localVgbmAssignments.length"
      :mode="TableMode.CLIENT_SIDE"
      :paginator="false"
    >
      <template
        v-for="field in textInputFields"
        #[`cell(${field})`]="{ data }"
        :key="field"
      >
        <PVInputText
          :disabled="isInputDisabled(data, field)"
          :invalid="!getInputState(data, field)"
          :on-blur="(event) => handleFormInput(data, field, event)"
          :value="data[field]"
          @value-change="(e) => handleFormInput(data, field, e, true)"
        ></PVInputText>
      </template>
      <template #cell(quality)="{ data }">
        <styled-multiselect
          v-if="data"
          :allow-empty="false"
          :disabled="isInputDisabled(data, 'quality')"
          :is-error="!getInputState(data, 'quality')"
          :options="qualityOptions"
          :model-value="data.quality"
          @select="handleFormInput(data, 'quality', $event)"
        >
          <template #singleLabel>
            <div :class="getSelectLabelClass(data.quality)">
              {{ getSelectLabel(data.quality) }}
            </div>
          </template>
          <template #option="option">
            <div :class="getSelectLabelClass(option.option.option)">
              {{ getSelectLabel(option.option.option) }}
            </div>
          </template>
        </styled-multiselect>
      </template>
    </table-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import StyledMultiselect from '../StyledMultiselect/StyledMultiselect.vue';
  import { cloneDeep, debounce } from 'lodash';
  import {
    IGetVgbmAssignmentResponseDto,
    QualityCheckStatus,
    VgbmAssignmentQuality,
  } from 'types';
  import {
    TableMode,
    TableFieldsBuilder,
    useTableWrapper,
  } from '../TableWrapper';
  import PVInputText from 'primevue/inputtext';
  import PVToggleSwitch from 'primevue/toggleswitch';

  export type FilledVgbmAssignment = IGetVgbmAssignmentResponseDto & {
    quality: VgbmAssignmentQuality;
  };
  export type AssignmentUpdateEvent =
    | {
        assignment: FilledVgbmAssignment;
        isValid: true;
      }
    | {
        assignment: IGetVgbmAssignmentResponseDto;
        isValid: false;
      };
  type EventOrValue = Event | string | undefined;

  const { t } = useI18n();

  const KKS_VGBM_CLASS = 'VGBM006';

  const props = defineProps<{
    pendingVgbmClasses?: string[];
    vgbmAssignments: IGetVgbmAssignmentResponseDto[];
  }>();

  const emit = defineEmits<{
    (
      e: 'assignmentChanged',
      assignmentUpdateEvent: AssignmentUpdateEvent,
    ): void;
  }>();

  const localVgbmAssignments = ref<IGetVgbmAssignmentResponseDto[]>(
    cloneDeep(props.vgbmAssignments),
  );
  const filteredVgbmAssignments = computed({
    get() {
      if (isHideEmpty.value) {
        return localVgbmAssignments.value.filter(
          (assignment) =>
            !!(assignment.value || assignment.unit || assignment.quality),
        );
      } else return localVgbmAssignments.value;
    },
    set(value) {
      localVgbmAssignments.value = value;
    },
  });
  const isHideEmpty = ref<boolean>(false);

  const { pageOptions } = useTableWrapper<IGetVgbmAssignmentResponseDto>(
    TableMode.CLIENT_SIDE,
    'class',
    undefined,
    undefined,
    false,
  );

  const tableFields = computed(() => {
    return new TableFieldsBuilder<IGetVgbmAssignmentResponseDto>({
      labelTranslationKey: 'kksVgbmForm',
      cellClassGetter: getTableRowClass,
      sortable: true,
    })
      .addField('description')
      .addField('class')
      .addField('value')
      .addField('unit', {
        width: ' 10%',
      })
      .addField('quality')
      .build();
  });

  const textInputFields: (keyof IGetVgbmAssignmentResponseDto)[] = [
    'value',
    'unit',
  ];

  const qualityOptions = computed<VgbmAssignmentQuality[]>(() => {
    return Object.values(VgbmAssignmentQuality).filter(
      (value) => typeof value === 'number',
    );
  });

  function getInputState(
    assignment: IGetVgbmAssignmentResponseDto,
    key: keyof IGetVgbmAssignmentResponseDto,
  ): boolean {
    /** Unit is not required for the input to be saved */
    if (key === 'unit') return true;
    return !!assignment[key];
  }

  function isAssignmentValid(
    assignment: IGetVgbmAssignmentResponseDto,
  ): boolean {
    return !!assignment.value && !!assignment.quality;
  }

  function getSelectLabel(option: VgbmAssignmentQuality): string {
    return `${option} - ${t(
      `kksVgbmForm.qualityOptions.${option}`,
    ).toString()}`;
  }

  function getSelectLabelClass(quality: QualityCheckStatus): string {
    return `quality-${quality}`;
  }

  function syncForm() {
    localVgbmAssignments.value = cloneDeep(props.vgbmAssignments);
  }

  function isInputDisabled(
    assignment: IGetVgbmAssignmentResponseDto,
    key: keyof IGetVgbmAssignmentResponseDto,
  ): boolean {
    return (
      /**
       * The KKS value cannot be edited because it's being treated as the
       * primary key.
       */
      (key === 'value' && assignment.class === KKS_VGBM_CLASS) ||
      (props.pendingVgbmClasses?.includes(assignment.class) ?? false)
    );
  }

  function getValueFromEvent(eventOrValue: EventOrValue): string {
    if (eventOrValue && typeof eventOrValue !== 'object') {
      return eventOrValue;
    } else if (
      eventOrValue instanceof FocusEvent &&
      eventOrValue.target instanceof HTMLInputElement
    ) {
      return eventOrValue.target.value;
    } else if (
      eventOrValue instanceof InputEvent &&
      eventOrValue.target instanceof HTMLInputElement
    ) {
      return eventOrValue.target.value;
    } else return '';
  }

  function handleFormInput(
    assignment: IGetVgbmAssignmentResponseDto,
    key: keyof IGetVgbmAssignmentResponseDto,
    eventOrValue: EventOrValue,
    isDebounced = false,
  ) {
    const value = getValueFromEvent(eventOrValue);
    if (!didAssignmentChange(assignment, key, value)) {
      return;
    }
    (assignment[key] as string | number) = value;
    if (isDebounced) {
      debouncedApplyFormInputChange(assignment);
    } else applyFormInputChange(assignment);
  }

  function applyFormInputChange(assignment: IGetVgbmAssignmentResponseDto) {
    const assignmentUpdateEvent: AssignmentUpdateEvent = {
      assignment: cloneDeep(assignment),
      isValid: isAssignmentValid(assignment),
    } as AssignmentUpdateEvent;
    emit('assignmentChanged', assignmentUpdateEvent);
  }

  const debouncedApplyFormInputChange = debounce(function (
    assignment: IGetVgbmAssignmentResponseDto,
  ) {
    applyFormInputChange(assignment);
  }, 800);

  function didAssignmentChange(
    assignment: IGetVgbmAssignmentResponseDto,
    key: keyof IGetVgbmAssignmentResponseDto,
    newValue: string,
  ) {
    return assignment[key] !== newValue;
  }

  function getTableRowClass(
    _value: IGetVgbmAssignmentResponseDto[keyof IGetVgbmAssignmentResponseDto],
    key: keyof IGetVgbmAssignmentResponseDto,
    item: IGetVgbmAssignmentResponseDto,
  ): string[] {
    if (
      (key === 'value' || key === 'unit' || key === 'quality') &&
      props.pendingVgbmClasses?.includes(item.class)
    ) {
      return ['busy'];
    } else return [];
  }

  watch(
    () => props.vgbmAssignments,
    () => {
      syncForm();
    },
    { immediate: true, deep: true },
  );
</script>

<style lang="scss" scoped>
  @include no-valid-input-icon;

  .hide-empty-switch-container {
    margin-bottom: 1rem;
    display: flex;
    justify-content: flex-end;
    column-gap: 0.5rem;
  }

  @mixin colors-by-quality {
    font-weight: 500;
    &:has(.quality-10) {
      color: $brick-red-dark-1;
    }
    &:has(.quality-20) {
      color: $brick-red-light-2;
    }
    &:has(.quality-40) {
      color: $dynamic-orange-light-2;
    }
    &:has(.quality-60) {
      color: $electric-yellow-light-2;
    }
    &:has(.quality-80) {
      color: $markish-green-dark-1;
    }
  }

  :deep(.multiselect__single) {
    @include colors-by-quality;
  }
  :deep(.multiselect__element) {
    @include colors-by-quality;
  }
  :deep(.multiselect__option) {
    @include colors-by-quality;
  }
</style>
